import mixpanel, { Dict } from 'mixpanel-browser';

import { IS_MIXPANEL_DEV_MODE_ENABLED, IS_PRODUCTION_BUILD } from '@/shared/environments';
import { getDeviceType } from '@/shared/lib/browser';
import { User } from '@/shared/types/User';

import { IS_MIXPANEL_DEBUGGER_ENABLED } from './constants';
import { ProjectData, useMixpanelPropsStore } from './useMixpanelPropsStore';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string;

// TODO move to abstraction slice [hash234123]
const MIXPANEL_ENABLED = !!MIXPANEL_TOKEN && (IS_PRODUCTION_BUILD || IS_MIXPANEL_DEV_MODE_ENABLED);

const deviceType = getDeviceType();

export const initMixpanel = () => {
  if (MIXPANEL_ENABLED) {
    mixpanel.init(MIXPANEL_TOKEN || '', { debug: false, record_sessions_percent: 1 });
    mixpanel.register({ deviceType });
  }
};

export const setMixpanelUser = (id: User['id'], email: User['email'], emailType: User['email_type']) => {
  if (MIXPANEL_ENABLED) {
    const distinctId: unknown = mixpanel.get_distinct_id();
    const mixpanelEmail: unknown = mixpanel.get_property('email');

    // Если уже установлен актуальный id, то ничего делать не надо
    if (distinctId !== id) {
      mixpanel.identify(id);
    }

    if (!mixpanelEmail || mixpanelEmail !== email) {
      mixpanel.people.set({ $email: email, emailType });
    }

    mixpanel.register({ email, emailType });
  }
};

export const shutdownMixpanel = () => {
  if (MIXPANEL_ENABLED) {
    mixpanel.reset();
  }
};

type EventPayload = {
  metadata?: {
    shouldSendRaskTTSData?: boolean;
    shouldSendSubscriptionData?: boolean;
  };
  eventName: string;
  properties?: object;
};

export const trackEvent = ({ eventName, metadata, properties }: EventPayload) => {
  const { projectData, subscriptionData } = useMixpanelPropsStore.getState();
  const mutableProjectData: Partial<ProjectData> = { ...projectData };

  if (mutableProjectData?.raskTTS && !metadata?.shouldSendRaskTTSData) {
    delete mutableProjectData.raskTTS;
  }

  const eventProperties = { ...subscriptionData, ...mutableProjectData, ...properties };

  if (MIXPANEL_ENABLED) {
    mixpanel.track(eventName, eventProperties);

    if (IS_MIXPANEL_DEBUGGER_ENABLED) {
      const emailType = mixpanel.get_property('emailType') as string;
      console.info('%cMixpanel debugger event:', 'color: #7F56D9');
      console.table({ deviceType, emailType, eventName, ...eventProperties });
    }
  }
};

export const setMixpanelUserProperty = (userId: string, prop: Dict) => {
  if (!MIXPANEL_ENABLED) {
    return;
  }

  const distinctId: unknown = mixpanel.get_distinct_id();

  if (!distinctId) {
    // mixpanel.identify must be called in order to associate the profile properties you set
    // with that user. You only need to call it once per page load for a given user.
    mixpanel.identify(userId);
  }

  mixpanel.people.set(prop);
};
